/**
 * Two tiny pure predicates used only to decide whether the wizard should
 * auto-skip an empty review step (see useImportWizard's submitMapping /
 * continueToRosterReview / backToSheetIssues / backToRosterReview).
 *
 * `conflictRows` mirrors the function of the same name in @discovr/ui's
 * importWizardHelpers.ts exactly. Duplicated rather than imported: this
 * package (business logic / hooks) doesn't otherwise depend on the ui
 * package, and pulling in a whole presentation package for one one-line
 * predicate isn't worth the layering violation. Keep in sync by hand if the
 * ui package's own row-completion rules change.
 */
import type { ImportBatchRow } from '@discovr/types';

/** Whether Sheet Review would ever have anything to show — a structural
 *  fact of the sheet (does any row have a duplicate group or a validation
 *  error), independent of whether those issues have since been resolved.
 *
 *  This is deliberately NOT "is everything currently resolved"
 *  (importWizardHelpers.ts's `isSheetReviewComplete` in the ui package) —
 *  conflating the two was a real bug here: by the time a batch reaches
 *  roster_review, AdvanceToRosterReview's own server-side validation has
 *  already GUARANTEED every row is resolved (that's its precondition), so
 *  an "is it resolved" check would always evaluate true and skip straight
 *  past Sheet Review to Mapping on the way back — even for a sheet that had
 *  real duplicates/errors the admin deliberately resolved and would
 *  reasonably expect to land back on. `hadSheetIssues` asks the right
 *  question instead: was there ever anything here at all — including a row
 *  that DID have a validation error but was since fixed (sheetResolution
 *  'fixed'): a successful fix clears ValidationErrors, so checking that
 *  alone would forget the row ever needed attention the moment it's fixed,
 *  which is exactly backwards from what this structural check is for. */
export function hadSheetIssues(rows: ImportBatchRow[]): boolean {
  return rows.some(
    (r) => r.duplicateGroupSize > 1 || Object.keys(r.validationErrors).length > 0 || r.sheetResolution === 'fixed',
  );
}

export function conflictRows(rows: ImportBatchRow[]): ImportBatchRow[] {
  return rows.filter((r) => r.rosterMatch !== null);
}
